import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { EditorContent, type JSONContent } from "@tiptap/react";
import { useRef, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { Heading } from "@/components/heading";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import {
	FieldContent,
	FieldDescription,
	FieldError,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEditor } from "@/editor/editor";
import { useFileUpload } from "@/hooks/useFileUpload";
import { cn } from "@/utils/cn";
import type { regrasQuery } from "./__generated__/regrasQuery.graphql";
import type { regrasUpdateMutation } from "./__generated__/regrasUpdateMutation.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(general)/moderacao/regras",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { club, session } = useRouteContext({ from: "/_logged/c/$club" });
	const { upload } = useFileUpload({ club: club.slug, session });

	const data = useLazyLoadQuery<regrasQuery>(
		graphql`
			query regrasQuery($club: String!) {
				club(slug: $club) {
					id
					rules {
						id
						title
						content
					}
				}
			}
		`,
		{ club: club.slug },
	);

	const rules = data.club.rules;

	const [title, setTitle] = useState(rules?.title ?? "");
	const [titleError, setTitleError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const editorImageRef = useRef<HTMLInputElement>(null);
	const editorVideoRef = useRef<HTMLInputElement>(null);

	const editor = useEditor({
		editable: true,
		content: rules?.content ? (rules.content as JSONContent) : undefined,
		placeholder: "Escreva as regras da sua comunidade aqui...",
		placeholderType: "first-line",
		session,
		club: club.slug,
		mimeTypes: ["*"],
		features: {
			colors: { enabled: true },
			button: { enabled: true },
			mention: { enabled: true },
			image: { enabled: true },
			iframe: { enabled: true },
			video: { enabled: true },
		},
	});

	const [updateRules] = useMutation<regrasUpdateMutation>(graphql`
		mutation regrasUpdateMutation($payload: UpdateRulesInput!) {
			updateRules(payload: $payload) {
				postEdge {
					node {
						id
						title
						content
					}
				}
			}
		}
	`);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (title.length < 3) {
			setTitleError("Título precisa ter no mínimo 3 caracteres");
			return;
		}
		if (title.length > 128) {
			setTitleError("Título só pode ter até 128 caracteres");
			return;
		}

		setTitleError(null);
		setIsSubmitting(true);

		updateRules({
			variables: {
				payload: {
					club: club.slug,
					title,
					content: JSON.stringify(editor?.getJSON()),
				},
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Regras atualizadas com sucesso!");
				setIsSubmitting(false);
			},
			onError: () => {
				toast.error(
					"Não foi possível atualizar as regras no momento. Tente novamente.",
				);
				setIsSubmitting(false);
			},
			updater: (store) => {
				const clubRecord = store.get(data.club.id);
				if (clubRecord) {
					clubRecord.invalidateRecord();
				}
			},
		});
	};

	return (
		<div className="size-full flex items-center justify-center py-4 md:py-8">
			<div className="p-6 w-full max-md:px-4 max-w-[700px] flex flex-col gap-6 border rounded-md">
				<Heading>Regras</Heading>

				<form onSubmit={handleSubmit} className="flex flex-col gap-6">
					<FieldContent>
						<FieldLabel htmlFor="title">Título</FieldLabel>
						{/* biome-ignore lint/correctness/useUniqueElementIds: this is valid */}
						<Input
							id="title"
							name="title"
							value={title}
							onChange={(e) => {
								setTitle(e.target.value);
								setTitleError(null);
							}}
							placeholder="Título das regras"
							aria-invalid={!!titleError}
						/>
						{titleError ? (
							<FieldError errors={[{ message: titleError }]} />
						) : (
							<FieldDescription>
								{title.length || 0} / 128 caracteres
							</FieldDescription>
						)}
					</FieldContent>

					<FieldContent>
						<FieldLabel>Conteúdo</FieldLabel>

						<div
							className={cn([
								"relative block w-full mt-3",
								"dark:before:hidden",
								"after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset sm:focus-within:after:ring-2 sm:focus-within:after:ring-blue-500",
								"[&_.ProseMirror]:outline-none",
							])}
						>
							<div
								className={cn(
									"file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input min-h-[180px] w-full min-w-0 rounded-md border bg-transparent px-2 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
									"focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
									"aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
									"flex flex-col",
								)}
							>
								<EditorContent
									editor={editor}
									className={cn(
										"relative block w-full appearance-none px-[calc(--spacing(2.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)]",
										"min-h-[200px] max-h-[600px] overflow-y-auto",
										"[&>div:first-child]:h-full [&>div:first-child>div:first-child]:h-full [&>div:first-child]:min-h-[190px] [&>div:first-child>div:first-child]:min-h-[190px]",
									)}
								/>
								<div className="flex mt-1 pointer-events-auto mb-1.5 pl-2 flex-row items-center justify-start gap-3">
									<input
										className="hidden"
										type="file"
										ref={editorImageRef}
										accept="image/*"
										onChange={(ev) => {
											if (ev.target.files?.length) {
												upload(ev.target.files[0]).then((res) => {
													editor?.commands.setMedia({
														fileId: res.id,
														title: res.name,
														width: "600",
														height: "600",
														dataAlign: "center",
													});
												});
											}

											if (editorImageRef.current) {
												editorImageRef.current.value = "";
											}
										}}
									/>

									<input
										className="hidden"
										type="file"
										ref={editorVideoRef}
										accept="video/*"
										onChange={(ev) => {
											if (ev.target.files?.length) {
												upload(ev.target.files[0]).then((res) => {
													if (res.muxId) {
														editor?.commands.setMuxVideo({
															fileId: res.id,
															assetId: res.muxId,
														});
													}
												});
											}

											if (editorVideoRef.current) {
												editorVideoRef.current.value = "";
											}
										}}
									/>

									<Tooltip>
										<TooltipTrigger type="button" asChild>
											<button
												type="button"
												className="hover:text-foreground text-muted-foreground"
												onClick={() =>
													editor?.chain().focus().toggleBold().run()
												}
											>
												<Icon name="Bold" className="size-4" />
											</button>
										</TooltipTrigger>
										<TooltipContent>Negrito</TooltipContent>
									</Tooltip>

									<Tooltip>
										<TooltipTrigger type="button" asChild>
											<button
												type="button"
												className="hover:text-foreground text-muted-foreground"
												onClick={() =>
													editor?.chain().focus().toggleItalic().run()
												}
											>
												<Icon name="Italic" className="size-4" />
											</button>
										</TooltipTrigger>
										<TooltipContent>Itálico</TooltipContent>
									</Tooltip>

									<div className="bg-border h-4 w-px min-w-px" />

									<Tooltip>
										<TooltipTrigger type="button" asChild>
											<button
												type="button"
												className="hover:text-foreground text-muted-foreground"
												onClick={() => editorImageRef.current?.click()}
											>
												<Icon name="Image" className="size-4" />
											</button>
										</TooltipTrigger>
										<TooltipContent>Adicionar Imagem</TooltipContent>
									</Tooltip>

									<Tooltip>
										<TooltipTrigger type="button" asChild>
											<button
												type="button"
												className="hover:text-foreground text-muted-foreground"
												onClick={() => editorVideoRef.current?.click()}
											>
												<Icon name="Video" className="size-4" />
											</button>
										</TooltipTrigger>
										<TooltipContent>Adicionar Video</TooltipContent>
									</Tooltip>
								</div>
							</div>
						</div>
					</FieldContent>

					<div className="flex mt-3 flex-row items-center justify-end w-full">
						<Button
							color="primary"
							disabled={isSubmitting}
							type="submit"
							className="flex flex-row items-center"
						>
							Salvar Regras {isSubmitting ? <Spinner /> : null}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
}
